import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/Layout";
import { ROUTE_PATHS } from "@/lib/index";
import { springPresets } from "@/lib/motion";
import { GitBranch, Package, CheckCircle, PlusCircle, Wrench, AlertCircle, Loader2, ChevronRight } from "lucide-react";

/* ── Typy ── */
interface ChangeEntry {
  type: "added" | "changed" | "fixed" | "removed" | "security";
  desc: string;
}

interface AppVersion {
  id: number;
  version: string;
  version_major: number;
  version_minor: number;
  version_patch: number;
  stage: "alpha" | "beta" | "rc" | "stable";
  released_at: string;
  summary: string;
  changelog: ChangeEntry[];
  is_current: boolean;
}

/* ── Konfiguracja etapów ── */
const STAGE_CONFIG = {
  alpha:  { label: "Alpha",  color: "rgba(220,150,80,0.85)",  bg: "rgba(220,150,80,0.08)",  border: "rgba(220,150,80,0.25)" },
  beta:   { label: "Beta",   color: "rgba(100,180,220,0.85)", bg: "rgba(100,180,220,0.08)", border: "rgba(100,180,220,0.25)" },
  rc:     { label: "RC",     color: "rgba(160,120,220,0.85)", bg: "rgba(160,120,220,0.08)", border: "rgba(160,120,220,0.25)" },
  stable: { label: "Stable", color: "rgba(100,200,120,0.85)", bg: "rgba(100,200,120,0.08)", border: "rgba(100,200,120,0.25)" },
};

const CHANGE_CONFIG = {
  added:    { icon: PlusCircle,   color: "rgba(100,200,120,0.8)",  label: "Dodano"    },
  changed:  { icon: Wrench,       color: "rgba(100,180,220,0.8)",  label: "Zmieniono" },
  fixed:    { icon: CheckCircle,  color: "rgba(160,120,220,0.8)",  label: "Naprawiono"},
  removed:  { icon: AlertCircle,  color: "rgba(220,80,80,0.8)",    label: "Usunięto"  },
  security: { icon: AlertCircle,  color: "rgba(212,175,55,0.85)",  label: "Bezpieczeństwo" },
};

/* ── Formatowanie daty ── */
function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("pl-PL", {
    year: "numeric", month: "long", day: "numeric"
  });
}

/* ── Badge etapu ── */
function StageBadge({ stage }: { stage: keyof typeof STAGE_CONFIG }) {
  const cfg = STAGE_CONFIG[stage];
  return (
    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full"
      style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}>
      {cfg.label}
    </span>
  );
}

/* ── Karta wersji ── */
function VersionCard({ v, index }: { v: AppVersion; index: number }) {
  const [open, setOpen] = useState(v.is_current);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...springPresets.gentle, delay: index * 0.07 }}
      className="relative"
    >
      {/* Linia osi czasu */}
      <div className="absolute left-[19px] top-14 bottom-0 w-px"
        style={{ background: "linear-gradient(to bottom, oklch(0.22 0.04 45 / 0.4), transparent)" }} />

      <div className="flex gap-5">
        {/* Węzeł osi czasu */}
        <div className="flex-shrink-0 mt-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center relative z-10"
            style={{
              background: v.is_current ? "rgba(212,175,55,0.12)" : "oklch(0.10 0.022 220)",
              border: `1.5px solid ${v.is_current ? "rgba(212,175,55,0.45)" : "oklch(0.20 0.03 220)"}`,
              boxShadow: v.is_current ? "0 0 16px rgba(212,175,55,0.12)" : "none",
            }}>
            <Package className="w-4 h-4"
              style={{ color: v.is_current ? "rgba(212,175,55,0.8)" : "rgba(130,150,175,0.5)" }} />
          </div>
        </div>

        {/* Treść karty */}
        <div className="flex-1 mb-8 rounded-2xl overflow-hidden"
          style={{
            background: v.is_current ? "oklch(0.11 0.028 220)" : "oklch(0.09 0.018 225)",
            border: `1px solid ${v.is_current ? "oklch(0.24 0.05 45)" : "oklch(0.17 0.025 220)"}`,
            boxShadow: v.is_current ? "0 4px 24px rgba(212,175,55,0.06)" : "none",
          }}>

          {/* Nagłówek karty */}
          <button className="w-full px-6 pt-5 pb-4 flex items-start justify-between gap-4 text-left hover:bg-white/[0.015] transition-colors duration-150"
            onClick={() => setOpen(o => !o)}>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap mb-1.5">
                <span className="text-xl font-bold font-mono tracking-tight"
                  style={{ color: v.is_current ? "rgba(212,175,55,0.88)" : "rgba(185,200,215,0.85)" }}>
                  v{v.version}
                </span>
                <StageBadge stage={v.stage} />
                {v.is_current && (
                  <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full"
                    style={{ color: "rgba(100,200,120,0.9)", background: "rgba(100,200,120,0.1)", border: "1px solid rgba(100,200,120,0.25)" }}>
                    ● Bieżąca
                  </span>
                )}
              </div>
              <p className="text-sm" style={{ color: "rgba(165,180,200,0.75)" }}>{v.summary}</p>
              <p className="text-xs mt-1" style={{ color: "rgba(110,125,145,0.55)" }}>
                {formatDate(v.released_at)}
              </p>
            </div>
            <motion.div animate={{ rotate: open ? 90 : 0 }} transition={{ duration: 0.2 }}
              className="flex-shrink-0 mt-1">
              <ChevronRight className="w-4 h-4" style={{ color: "rgba(130,145,165,0.5)" }} />
            </motion.div>
          </button>

          {/* Lista zmian */}
          {open && v.changelog.length > 0 && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }}
              className="px-6 pb-5 border-t" style={{ borderColor: "oklch(0.15 0.022 220)" }}>
              <div className="pt-4 space-y-2">
                {v.changelog.map((item, i) => {
                  const cfg = CHANGE_CONFIG[item.type] ?? CHANGE_CONFIG.added;
                  const Icon = cfg.icon;
                  return (
                    <div key={i} className="flex items-start gap-3">
                      <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: cfg.color }} />
                      <div className="flex gap-2 items-baseline flex-wrap">
                        <span className="text-[10px] font-semibold uppercase tracking-wider"
                          style={{ color: cfg.color }}>{cfg.label}</span>
                        <span className="text-sm" style={{ color: "rgba(165,180,200,0.75)" }}>{item.desc}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ════════════════════════════════════════════════════════
   GŁÓWNA STRONA
════════════════════════════════════════════════════════ */
export default function VersionsPage() {
  const [versions, setVersions] = useState<AppVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState<AppVersion | null>(null);

  useEffect(() => {
    supabase
      .from("app_versions")
      .select("*")
      .order("released_at", { ascending: false })
      .then(({ data }) => {
        if (data) {
          setVersions(data as AppVersion[]);
          setCurrent((data as AppVersion[]).find(v => v.is_current) ?? null);
        }
        setLoading(false);
      });
  }, []);

  return (
    <Layout>
      <div className="min-h-screen py-24 px-4"
        style={{ background: "linear-gradient(180deg, oklch(0.09 0.015 235) 0%, oklch(0.11 0.025 225) 100%)" }}>
        <div className="container mx-auto max-w-2xl">

          {/* Nagłówek */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle} className="mb-14">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
            </div>
            <div className="flex items-center justify-center gap-3 mb-3">
              <GitBranch className="w-5 h-5" style={{ color: "rgba(212,175,55,0.6)" }} />
              <p className="text-xs uppercase tracking-[0.3em]" style={{ color: "rgba(140,155,175,0.6)" }}>
                Historia wydań
              </p>
            </div>
            <h1 className="text-4xl font-bold text-center mb-3"
              style={{ fontFamily: "'Playfair Display', serif", color: "rgba(212,175,55,0.75)" }}>
              Changelog
            </h1>

            {/* Aktualna wersja — hero badge */}
            {current && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.15 }}
                className="flex justify-center mt-6">
                <div className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl"
                  style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.22)", backdropFilter: "blur(8px)" }}>
                  <div className="flex flex-col items-center">
                    <span className="text-xs uppercase tracking-widest mb-0.5" style={{ color: "rgba(140,155,175,0.55)" }}>
                      Aktualna wersja
                    </span>
                    <span className="text-2xl font-bold font-mono" style={{ color: "rgba(212,175,55,0.85)" }}>
                      v{current.version}
                    </span>
                  </div>
                  <div className="w-px h-10" style={{ background: "rgba(212,175,55,0.18)" }} />
                  <div className="flex flex-col gap-1">
                    <StageBadge stage={current.stage} />
                    <span className="text-[10px]" style={{ color: "rgba(120,135,155,0.55)" }}>
                      {formatDate(current.released_at)}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* SemVer opis */}
            <p className="text-center text-xs mt-5 leading-relaxed" style={{ color: "rgba(110,125,145,0.55)" }}>
              Wersjonowanie wg standardu{" "}
              <a href="https://semver.org/lang/pl/" target="_blank" rel="noopener noreferrer"
                className="underline underline-offset-2 hover:opacity-80 transition-opacity"
                style={{ color: "rgba(140,155,175,0.6)" }}>
                Semantic Versioning 2.0
              </a>
              {" "}— MAJOR.MINOR.PATCH-STAGE
            </p>
          </motion.div>

          {/* Lista wersji */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: "rgba(212,175,55,0.4)" }} />
            </div>
          ) : (
            <div className="relative">
              {versions.map((v, i) => (
                <VersionCard key={v.id} v={v} index={i} />
              ))}
            </div>
          )}

          {/* Legenda typów zmian */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            className="mt-8 p-5 rounded-2xl"
            style={{ background: "oklch(0.09 0.018 225)", border: "1px solid oklch(0.17 0.025 220)" }}>
            <p className="text-xs uppercase tracking-widest mb-3" style={{ color: "rgba(120,135,155,0.5)" }}>
              Legenda typów zmian
            </p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(CHANGE_CONFIG).map(([key, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <div key={key} className="flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                    <span className="text-xs" style={{ color: "rgba(140,155,175,0.6)" }}>
                      <span className="font-medium" style={{ color: cfg.color }}>{cfg.label}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </motion.div>

          <div className="text-center mt-8">
            <Link to={ROUTE_PATHS.HOME}
              className="text-xs transition-opacity hover:opacity-70"
              style={{ color: "rgba(120,135,155,0.5)" }}>
              ← Wróć na stronę główną
            </Link>
          </div>

        </div>
      </div>
    </Layout>
  );
}
